//******************************************************************************************
// QuestionAnswerManagementPopup Component: Provides an interface for managing a list of
// question-answer pairs. Supports adding, deleting, and reordering entries with swap
// functionality for prioritizing questions.
//******************************************************************************************

import React, { useState, useEffect } from "react";
import "./QuestionAnswerManagementPopup.scss";
import axios from "axios";
import { FiTrash2, FiArrowUp, FiArrowDown } from "react-icons/fi";
import { FaRegCommentDots, FaQuestionCircle } from "react-icons/fa";

const QuestionAnswerManagementPopup = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("welcome");
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({ question: "", answer: "" });
  const [message, setMessage] = useState("");
  const [startingMessages, setStartingMessages] = useState({
    active: "",
    inactive: "",
    secondary: "",
    secondaryEnabled: false,
  });

  // Fetch initial entries when the component mounts
  useEffect(() => {
    fetchEntries();
    fetchStartingMessages();
  }, []);

  const fetchEntries = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL("/api/config/questions", baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEntries(response.data.config.entries || []);
      } else {
        setMessage("Falha ao carregar entradas.");
      }
    } catch (error) {
      console.error("Error fetching Q&A entries:", error);
      setMessage("Erro ao carregar entradas.");
    }
  };

  const fetchStartingMessages = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/config/starting-messages`;
      const token = localStorage.getItem("admin-token");
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setStartingMessages(response.data.startingMessages);
      }
    } catch (error) {
      console.error("Error fetching starting messages:", error);
      setMessage("Erro ao carregar mensagens de boas-vindas.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value.length <= 140) {
      setNewEntry((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.question || !newEntry.answer) {
      setMessage("Preencha questão e resposta.");
      return;
    }
    if (entries.length >= 10) {
      setMessage("Máximo de 10 entradas atingido.");
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL("/api/config/questions/add", baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.put(apiUrl, { question: newEntry.question, answer: newEntry.answer }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        setEntries(response.data.config.entries);
        setNewEntry({ question: "", answer: "" });
        setMessage("Entrada adicionada com sucesso.");
      } else {
        setMessage("Falha ao adicionar entrada.");
      }
    } catch (error) {
      console.error("Error adding entry:", error);
      setMessage("Erro ao adicionar entrada.");
    }
  };

  const handleDeleteEntry = async (index) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL(`/api/config/questions/${index}`, baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.delete(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEntries(response.data.config.entries);
        setMessage("Entrada apagada com sucesso.");
      } else {
        setMessage("Falha ao apagar entrada.");
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      setMessage("Erro ao apagar entrada.");
    }
  };

  const handleSaveMessages = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/config/starting-messages`;
      const token = localStorage.getItem("admin-token");
      const response = await axios.put(apiUrl, startingMessages, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setMessage("Mensagens de boas-vindas atualizadas com sucesso.");
      } else {
        setMessage("Falha ao atualizar as mensagens de boas-vindas.");
      }
    } catch (error) {
      console.error("Error updating starting messages:", error);
      setMessage("Erro ao salvar mensagens.");
    }
  };

  const handleSwapEntries = async (index1, index2) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL("/api/config/questions/swap", baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.put(apiUrl, { index1, index2 }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        setEntries(response.data.config.entries);
        setMessage("Ordem alterada com sucesso.");
      } else {
        setMessage("Falha ao alterar a ordem.");
      }
    } catch (error) {
      console.error("Error swapping entries:", error);
      setMessage("Falha ao alterar a ordem.");
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    handleSwapEntries(index, index - 1);
  };

  const handleMoveDown = (index) => {
    if (index === entries.length - 1) return;
    handleSwapEntries(index, index + 1);
  };

  // ----- TABS RENDERING -----
  const renderTabs = () => (
    <div className="qa-management-popup__tabs">
      <div className={`qa-management-popup__tab ${activeTab === "welcome" ? "active" : ""}`} onClick={() => setActiveTab("welcome")}>
        <FaRegCommentDots />
        <span>Mensagens</span>
      </div>
      <div className={`qa-management-popup__tab ${activeTab === "faq" ? "active" : ""}`} onClick={() => setActiveTab("faq")}>
        <FaQuestionCircle />
        <span>FAQs</span>
      </div>
    </div>
  );

  const renderWelcomeTab = () => (
    <div className="qa-management-popup__tab-body">
      <h3>Mensagens de Boas-Vindas</h3>
      <div className="qa-management-popup__form-group">
        <label>Mensagem Ativa:</label>
        <textarea value={startingMessages.active} onChange={(e) => setStartingMessages((prev) => ({ ...prev, active: e.target.value }))} maxLength="300" />
      </div>
      <div className="qa-management-popup__form-group">
        <label>Mensagem Inativa:</label>
        <textarea value={startingMessages.inactive} onChange={(e) => setStartingMessages((prev) => ({ ...prev, inactive: e.target.value }))} maxLength="300" />
      </div>
      <div className="qa-management-popup__form-group">
        <label>Mensagem Secundária (Opcional):</label>
        <textarea
          value={startingMessages.secondary}
          onChange={(e) => setStartingMessages((prev) => ({ ...prev, secondary: e.target.value }))}
          maxLength="300"
        />
        <label className="qa-management-popup__checkbox-label">
          <input
            type="checkbox"
            checked={startingMessages.secondaryEnabled}
            onChange={(e) =>
              setStartingMessages((prev) => ({
                ...prev,
                secondaryEnabled: e.target.checked,
              }))
            }
          />
          Ativar Mensagem Secundária
        </label>
      </div>
      <button className="qa-management-popup__save-button" onClick={handleSaveMessages}>
        Salvar Mensagens
      </button>
    </div>
  );

  const renderFAQTab = () => (
    <div className="qa-management-popup__tab-body">
      <h3>Gestão de FAQs</h3>
      <div className={`qa-management-popup__message ${message ? "visible" : ""}`} aria-live="polite">
        {message && <p>{message}</p>}
      </div>
      <div className="qa-management-popup__input-container">
        <textarea
          name="question"
          placeholder="Escreva a questão (máx. 140 caracteres)"
          value={newEntry.question}
          onChange={handleInputChange}
          aria-label="Enter question"
          maxLength="140"
        />
        <textarea
          name="answer"
          placeholder="Escreva a resposta (máx. 140 caracteres)"
          value={newEntry.answer}
          onChange={handleInputChange}
          aria-label="Enter answer"
          maxLength="140"
        />
        <button onClick={handleAddEntry} disabled={entries.length >= 10} aria-disabled={entries.length >= 10}>
          Adicionar
        </button>
      </div>

      <ul className="qa-management-popup__list">
        {(entries || []).map((entry, index) => (
          <li key={index} className="qa-management-popup__item">
            <div className="qa-management-popup__priority">
              <button onClick={() => handleMoveUp(index)} disabled={index === 0} aria-label="Move up" className="qa-management-popup__arrow">
                <FiArrowUp />
              </button>
              <span className="qa-management-popup__priority-number">{index + 1}</span>
              <button
                onClick={() => handleMoveDown(index)}
                disabled={index === entries.length - 1}
                aria-label="Move down"
                className="qa-management-popup__arrow"
              >
                <FiArrowDown />
              </button>
            </div>
            <span className="qa-management-popup__item-text">
              <strong>Q:</strong> {entry.question} <br />
              <strong>A:</strong> {entry.answer}
            </span>
            <button
              onClick={() => handleDeleteEntry(index)}
              className="qa-management-popup__delete"
              title="Delete entry"
              aria-label={`Delete question ${index + 1}`}
            >
              <FiTrash2 />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="qa-management-popup">
      <div className="qa-management-popup__overlay" onClick={onClose} aria-label="Close popup overlay" />
      <div className="qa-management-popup__content" role="dialog" aria-labelledby="qa-popup-title">
        {/* Tabs Header */}
        {renderTabs()}
        {/* Tab Content */}
        {activeTab === "welcome" && renderWelcomeTab()}
        {activeTab === "faq" && renderFAQTab()}
      </div>
    </div>
  );
};

export default QuestionAnswerManagementPopup;
